import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ReferralCodeResponseDto,
  ReferralSummaryResponseDto,
} from '@nomiss/services';
import {
  getMyReferralCode,
  getMyReferralSummary,
  redeemReferralCode,
  resolveAccessToken,
  type RedeemReferralResult,
  type ReferralError,
} from '@nomiss/services';
import { subscriptionQueryKeys } from './use-subscription';

export const referralQueryKeys = {
  me: ['referral', 'me'] as const,
  code: ['referral', 'code'] as const,
};

export function useMyReferralSummary(
  accessToken?: string | null,
  enabled = true,
) {
  const token = accessToken ?? resolveAccessToken();

  return useQuery({
    queryKey: [...referralQueryKeys.me, token ?? ''],
    queryFn: () => getMyReferralSummary(),
    enabled: enabled && Boolean(token),
  });
}

export function useMyReferralCode(accessToken?: string | null, enabled = true) {
  const token = accessToken ?? resolveAccessToken();

  return useQuery({
    queryKey: [...referralQueryKeys.code, token ?? ''],
    queryFn: () => getMyReferralCode(),
    enabled: enabled && Boolean(token),
  });
}

export function useRedeemReferral() {
  const queryClient = useQueryClient();

  return useMutation<RedeemReferralResult, ReferralError, string>({
    mutationFn: (code: string) => redeemReferralCode(code),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: referralQueryKeys.me });
      void queryClient.invalidateQueries({ queryKey: referralQueryKeys.code });
      void queryClient.invalidateQueries({
        queryKey: subscriptionQueryKeys.me,
      });
    },
  });
}

export type {
  ReferralCodeResponseDto,
  ReferralSummaryResponseDto,
  RedeemReferralResult,
};
